import { revertActionDispatches } from 'ai-constants'
import {
  ConditionContext,
  InspectionFunction,
  InspectionVariable,
  TemplateCondition,
  TemplateConditionAction,
  TemplateConditionTarget,
  TemplateInput,
  TemplateVariable,
} from 'bff'
import { meetConditionTerms } from './calculate-conditions'

type TemplateInputMinimal = Pick<
  TemplateInput,
  'value' | 'identifier' | 'type' | 'target'
>

export interface TemplateVisibility {
  type: 'input' | 'image'
  value: string[]
}

export interface IActionGenerate {
  section: string
  assetType: 'inputs' | 'images'
  images?: {
    count: number
    side: {
      type: 'value' | 'reference'
      value?: string
      image?: string
    }
    context?: {
      explanation: string
      filename: string
    }
  }
  inputs?: TemplateInputMinimal[]
}

export type IConditionActions = {
  event: 'show' | 'hide' | 'navigate'
  detail: TemplateConditionTarget
}

export class InspectionConditionHelper {
  public static checkConditions<
    TCondition extends TemplateCondition,
    TInput extends TemplateInputMinimal,
  >({
    conditions,
    inputs,
    conditionContext,
    inspectionVariables = [],
    templateVariables = [],
    inspectionFunctions,
  }: {
    conditions: TCondition[]
    inputs: TInput[]
    conditionContext?: ConditionContext
    inspectionVariables: InspectionVariable[]
    templateVariables: TemplateVariable[]
    inspectionFunctions: InspectionFunction[]
  }) {
    const events: IConditionActions[] = []

    conditions.forEach((condition) => {
      const conditionPass = meetConditionTerms({
        conditionContext,
        inputs,
        link: condition.link,
        terms: condition.terms,
        variables: templateVariables.map((templateVariable) => {
          return {
            type: templateVariable.type,
            name: templateVariable.name,
            value: inspectionVariables.find(
              (inspectionVariable) =>
                inspectionVariable.name === templateVariable.name,
            )?.value,
          }
        }),
        functions: inspectionFunctions,
      })

      const actions = condition.actions
        .filter(
          (
            action,
          ): action is Omit<TemplateConditionAction, 'dispatch'> & {
            dispatch: 'show' | 'hide' | 'navigate'
          } =>
            action.dispatch === 'show' ||
            action.dispatch === 'hide' ||
            action.dispatch === 'navigate',
        )
        .map((action): IConditionActions => {
          if (conditionPass)
            return {
              event: action.dispatch,
              detail: action.target,
            }

          const reverse = revertActionDispatches[action.dispatch]

          return {
            event: reverse,
            detail: action.target,
          }
        })

      events.push(...actions)
    })

    return events.map((event, index) => ({
      ...event,
      detail: {
        ...event.detail,
        value: event.detail.value?.filter(
          (asset) =>
            !events.find(
              (e, eventIterIndex) =>
                e.event === 'show' &&
                e.detail.value.includes(asset) &&
                eventIterIndex !== index,
            ),
        ),
      },
    }))
  }
}
